import { useState, type ReactNode } from 'react'
import { MAP_HEIGHT_CLASS } from '@/components/MapPlaceholder'
import type { ViewMode } from '@/utils/urlState'

interface ResultsLayoutProps {
	viewMode: ViewMode
	/** Card list; not rendered in map view. */
	listContent: ReactNode
	/** Map panel; keeps its position in the tree across views so the embed never reloads. */
	mapContent: ReactNode
	/** Split view: stretch the list column to the map height (empty/error states). */
	listFillHeight?: boolean
	className?: string
}

/**
 * Lays out the list and map for every view mode. The map is one persistent element that
 * CSS moves between split (side column), map (full width) and grid (hidden). Switching views
 * then only changes classes, so the SightMap iframe and Google map stay loaded.
 */
export function ResultsLayout ({ viewMode, listContent, mapContent, listFillHeight = false, className = '' }: ResultsLayoutProps) {
	// Mount the map the first time a map view shows, then keep it. Grid-only visitors never load it.
	const [mapActivated, setMapActivated] = useState(viewMode !== 'grid')
	if (viewMode !== 'grid' && !mapActivated) setMapActivated(true)

	// max-width caps the split list column; flexbox redistributes the leftover width to the map.
	const listClasses: Record<Exclude<ViewMode, 'map'>, string> = {
		grid: 'spaces:w-full spaces:min-w-0',
		split: listFillHeight
			? `spaces:flex-1 spaces:min-w-0 spaces:max-w-[720px] spaces:md:flex spaces:md:flex-col ${MAP_HEIGHT_CLASS}`
			: 'spaces:flex-1 spaces:min-w-0 spaces:max-w-[720px]'
	}
	const mapClasses: Record<ViewMode, string> = {
		grid: 'spaces:hidden',
		// Hidden on mobile (split view shouldn't render on mobile).
		split: `spaces:hidden spaces:md:block spaces:flex-1 spaces:min-w-[400px] spaces:sticky spaces:top-16 ${MAP_HEIGHT_CLASS}`,
		map: `spaces-map-view spaces:w-full ${MAP_HEIGHT_CLASS}`
	}

	const classes = [
		'spaces-results-layout spaces:flex spaces:flex-col spaces:md:flex-row spaces:gap-16 spaces:items-start',
		viewMode === 'split' ? 'spaces-split-view' : '',
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={classes}>
			{viewMode !== 'map' && <div className={listClasses[viewMode]}>{listContent}</div>}
			{mapActivated && <div className={mapClasses[viewMode]}>{mapContent}</div>}
		</div>
	)
}
